import { type Readable, type Writable, readonly, writable } from "svelte/store";

import { getFirstInHierarchy } from "@-/toolbox/lib.ts";

export interface RouteProps {
	params: URLPatternResult;
}

let abortController = new AbortController();

class State {
	/** currently navigating url */
	url = $state(location.href);

	/** is a new route loading */
	loading = $state(false);

	/** whether navigation was interrupted. resets every time it's signaled */
	signal = $state.raw(abortController.signal);

	/** the "root" RouteGroup context */
	ctx: GroupContext | undefined;
}
export const route = new State();

export class GroupContext {
	#patterns: { pattern: URLPattern; match: Writable<URLPatternResult | undefined> }[] = [];
	#children: GroupContext[] = [];

	/** current route's pattern */
	pattern = $state.raw<URLPattern>();

	internalAdd(input: URLPatternInput): Readable<URLPatternResult | undefined> {
		const pattern = new URLPattern(input, location.origin);
		const match = writable<URLPatternResult | undefined>();

		this.#patterns.push({ pattern, match });
		return readonly(match);
	}

	internalRoute(url: string) {
		this.pattern = undefined;

		for (const { pattern, match } of this.#patterns) {
			const tryMatch = pattern.exec(url);
			if (!tryMatch) {
				match.set(undefined);
				continue;
			}

			match.set(tryMatch);
			this.pattern = pattern;
		}

		for (const child of this.#children) child.internalRoute(url);
	}

	internalNest(ctx: GroupContext) {
		this.#children.push(ctx);
	}

	internalUnnest(ctx: GroupContext) {
		this.#children.splice(this.#children.indexOf(ctx), 1);
	}
}
export { getCurrentRouteGroup } from "./RouteGroup.svelte";

interface NavigationState {
	focus?: string;
}

/** needs to be called in a svelte component context */
export const setupRouter = () => {
	let { promise: loaded, resolve: finishedLoading } = Promise.withResolvers<void>();

	const finishLoading = () => {
		if (import.meta.env.DEV) console.debug("[router] navigation finished");
		route.loading = false;
		finishedLoading();

		const { promise, resolve } = Promise.withResolvers<void>();
		loaded = promise;
		finishedLoading = resolve;
	};

	navigation.addEventListener("navigate", event => {
		if (!event.canIntercept) return;
		if (event.hashChange || event.downloadRequest != null || event.formData != null) return;

		const activeElement = document.activeElement;
		if (activeElement) {
			const focus = getFirstInHierarchy(activeElement, e => e.id);
			if (focus) {
				// oxlint-disable-next-line typescript/no-unsafe-type-assertion
				const currentState = navigation.currentEntry?.getState() as
					Record<string, unknown> | undefined;

				navigation.updateCurrentEntry({
					state: { ...currentState, focus } satisfies NavigationState,
				});
			}
		}

		if (import.meta.env.DEV)
			console.debug(
				"[router] navigation started",
				event.destination.url,
				navigation.currentEntry?.getState(),
			);

		route.url = event.destination.url;
		route.loading = true;
		route.ctx!.internalRoute(route.url);
		dispatchEvent(new Event("router:loading"));

		const abortListener = () => {
			if (import.meta.env.DEV) console.debug("[router] navigation aborted", event.signal.reason);

			event.signal.removeEventListener("abort", abortListener);
			abortController.abort(event.signal.reason);
			dispatchEvent(new Event("router:done"));

			// re-arm the signal for future navigations
			abortController = new AbortController();
			route.signal = abortController.signal;
		};
		event.signal.addEventListener("abort", abortListener);

		event.intercept({
			async handler() {
				await loaded;
				event.signal.removeEventListener("abort", abortListener);

				// oxlint-disable-next-line typescript/no-unsafe-type-assertion
				const destination = event.destination.getState() as NavigationState | undefined;
				if (destination?.focus) {
					const el = document.getElementById(destination.focus);
					if (import.meta.env.DEV)
						console.debug("[router] restoring focus to", { focused: destination.focus, el });
					el?.focus();
				}

				event.scroll();
				document.dispatchEvent(new Event("router:done"));
			},
		});
	});

	$effect(() => {
		if ($effect.pending() == 0 && route.loading) {
			finishLoading();
		}
	});
};
